import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {from, Observable, throwError} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {AccountService, HttpCancelService} from '../service';
import {TranslateService} from '@ngx-translate/core';
import {SpinnerService} from '@shared-lib';
import {DialogService} from 'primeng/dynamicdialog';
import {X_ERROR_MESSAGE} from '../../shared/constants/app-constants';
import {ManagedErrorCode} from './managed-error-code';
import {restServices} from '../../rest-services';
import {DialogsUtilsService} from '../service/utils/dialogs-utils.service';
import {environment} from '../../../environments/environment';
import {SKIP_ERROR_HANDLING} from './skip-error-handling.token';

@Injectable({providedIn: 'root'})
export class ErrorInterceptor implements HttpInterceptor {

	private router = inject(Router);

	constructor(private httpCancelService: HttpCancelService,
							private translate: TranslateService,
							private spinner: SpinnerService,
							private dialogService: DialogService,
							private accountService: AccountService,
							private dialogUtils: DialogsUtilsService) {
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(map((event: HttpEvent<any>) => {
				return event;
			}), catchError((errorResponse: HttpErrorResponse) => {
				if (request.context.get(SKIP_ERROR_HANDLING)) {
					// chiamata di background non critica --> nessun popup, nessuna cancellazione delle altre request in corso
					return throwError(() => errorResponse);
				}
				// gli endpoint che tornano file chiamano con responseType 'blob', e Angular incapsula in un Blob
				// ANCHE il body d'errore: va riletto come JSON, sennò errorKey resta undefined e qualsiasi 400
				// finisce nel ramo generico invece che nel suo case
				if (errorResponse.error instanceof Blob) {
					return from(errorResponse.error.text()).pipe(
						mergeMap(testo => this.gestisciErrore(request, this.conBodyJson(errorResponse, testo)))
					);
				}
				return this.gestisciErrore(request, errorResponse);
			})
		);
	}

	/**
	 * Rigenera la HttpErrorResponse con il body deserializzato al posto del Blob.
	 * Se il contenuto non è JSON si tiene l'originale: meglio il ramo generico che un'eccezione qui dentro.
	 */
	private conBodyJson(errorResponse: HttpErrorResponse, testo: string): HttpErrorResponse {
		try {
			return new HttpErrorResponse({
				error: JSON.parse(testo),
				headers: errorResponse.headers,
				status: errorResponse.status,
				statusText: errorResponse.statusText,
				url: errorResponse.url ?? undefined,
			});
		} catch {
			return errorResponse;
		}
	}

	/**
	 * Gestione dell'errore in base allo status: mostra il dialog opportuno e rilancia.
	 */
	private gestisciErrore(request: HttpRequest<any>, errorResponse: HttpErrorResponse): Observable<never> {
		this.spinner.stop(); // stoppo eventuale spinner in corso...
		this.httpCancelService.cancelPendingRequests();
		// this.dialogService.dialogComponentRefMap.forEach(dialog => dialog.destroy()); // chiusura di tutti i p-dialog eventualmente aperti
		switch (errorResponse.status) {
		case 0:
			if (request.url.includes(restServices.userInfo) || request.url.includes('/api') || request.url.includes('/services')) {
				// errore "particolare" sessione scaduta KAA --> rimando a login
				// Gestisce il Redirect (302) a login page preso dopo che Spring Security
				// blocca una chiamata a /api perchè trova il cookie non valido, ma poi la chiamata
				// ajax che si attende un json e prende un 200 con html xkè ha seguito la redirect, va in errore.
				this.logoutAndGoToLogin(true);
			} else {
				this.manageError(errorResponse);
			}
			break;
		case 400:
			// N.B.: il caso speciale dei documenti non serve più - il body d'errore dei blob viene
			// deserializzato prima di arrivare qui, quindi documentoNotFound passa dal suo case
			this.manageError400(errorResponse);
			break;
		case 200:
		case 401:
			// 200 OK - succede se si tira giù dominio: azure ritorna direttamente l'html dell'index.html
			// credenziali errate o sessione scaduta --> logout e redirect a login
			if (!request.url.includes('kaa/login')) {
				this.logoutAndGoToLogin(true);
			} else {
				this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), this.translate.instant('kaa_401'));
			}
			break;
		case 403:
			// accesso negato ad un servizio chiamato --> redirect a dashboard principale
			this.closeAllDialogs();
			this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), this.translate.instant('error_authorities'));
			this.router.navigate(['/applicazioni'], {state: {showDialog: true}});
			break;
		case 500:
		case 504:
			if (request.url.includes('/user-info')) {
				// gestione "oscena" dominio down --> forzo logout dell'utente
				this.logoutAndGoToLogin();
			} else {
				// errori "fatali" --> mostro messaggio generale e torno alla home
				this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), this.translate.instant('fatal_error'), 'error', true);
			}
			break;
		case 503:
			// service unavailable --> rimando alla pagina statica di manutenzione servita da Apache (fuori dalla SPA)
			this.closeAllDialogs();
			this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), this.translate.instant('fatal_error'), 'error', true);
			// window.location.href = '/maintenance'; // FIXME - INNESCA UN LOOP! da capire come risolvere
			this.router.navigate(['login'], {state: {showDialog: true}});
			break;
		default:
			console.error('ERROR!', errorResponse);
			this.manageError(errorResponse);
		}
		return throwError(() => errorResponse);
	}

	/**
	 * Gestione errori generici
	 */
	private manageError(errorResponse: HttpErrorResponse) {
		const errMsgHeader = errorResponse.headers.get(X_ERROR_MESSAGE);
		let errorDescription: string;
		if (errMsgHeader) {
			errorDescription = errMsgHeader;
		} else if (errorResponse.error?.detail) {
			errorDescription = errorResponse.error.detail;
		} else {
			errorDescription = errorResponse.error?.title ?? errorResponse.message;
		}
		this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), errorDescription);
	}

	/**
	 * Gestione errori 400 - specifico per codice errore
	 */
	private manageError400(errorResponse: HttpErrorResponse) {
		const errorKey: string = errorResponse.error?.errorKey;
		let errorMessage: string;
		switch (errorKey) {
			case ManagedErrorCode.badRequest:
				errorMessage = this.translate.instant('error_400');
				break;
			case ManagedErrorCode.userRegistrato:
				errorMessage = this.translate.instant('error_userRegistrato');
				break;
			case ManagedErrorCode.totQuoteIsNot100:
				errorMessage = this.translate.instant('error_totQuoteIsNot100');
				break;
			case ManagedErrorCode.max100QuotaReale:
				errorMessage = this.translate.instant('error_max100QuotaReale');
				break;
			case ManagedErrorCode.quoteNotNull:
				errorMessage = this.translate.instant('error_quoteNotNull');
				break;
			case ManagedErrorCode.patrimNetAziendals0:
				errorMessage = this.translate.instant('error_patrimNetAziendals0');
				break;
			case ManagedErrorCode.pensioneNull:
				errorMessage = this.translate.instant('error_pensioneNull');
				break;
			case ManagedErrorCode.pensioneConiugeNull:
				errorMessage = this.translate.instant('error_pensioneConiugeNull');
				break;
			case ManagedErrorCode.errGetData:
				errorMessage = this.translate.instant('error_errGetData');
				break;
			case ManagedErrorCode.numDipendentiNull:
				errorMessage = this.translate.instant('error_numDipendentiNull');
				break;
			case ManagedErrorCode.erroreGenReportEPReporter:
				errorMessage = this.translate.instant('error_genReportEPReporter');
				break;
			case ManagedErrorCode.erroreGenReportDownload:
				errorMessage = this.translate.instant('error_genReportDownload');
				break;
			case ManagedErrorCode.activationUserError:
				errorMessage = this.translate.instant('error_activationUser');
				break;
			case ManagedErrorCode.errorEndDate:
				errorMessage = this.translate.instant('error_errorEndDate');
				break;
			case ManagedErrorCode.ephesoError:
				errorMessage = errorResponse.error?.defaultMessage;
				break;
			case ManagedErrorCode.testoRegolaAlreadyExists:
				errorMessage = this.translate.instant('error_testoRegolaAlreadyExists');
				break;
			case ManagedErrorCode.nodoConQuotePatrimoni:
				errorMessage = this.translate.instant('error_nodoConQuotePatrimoni');
				break;
			case  ManagedErrorCode.knowHowAlreadyExists:
				errorMessage = this.translate.instant('error_knowHowAlreadyExists');
				break;
			case ManagedErrorCode.fileNotSafeError:
				errorMessage = this.translate.instant('error_fileNotSafeError');
				break;
			case ManagedErrorCode.contattoDossierAlreadyExists:
				errorMessage = this.translate.instant('error_contattoDossierAlreadyExists');
				break;
			case ManagedErrorCode.documentoNotFound:
				errorMessage = this.translate.instant('error_documentoNotFound');
				break;
			case ManagedErrorCode.noImmobiliCatasto:
				errorMessage = this.translate.instant('error_noImmobiliCatasto');
				break;
			case ManagedErrorCode.captchaErrato:
				errorMessage = this.translate.instant('error_captchaErrato');
				break;
			case ManagedErrorCode.walletEsistente:
				errorMessage = this.translate.instant('error_walletEsistente');
				break;
			case ManagedErrorCode.walletNotFound:
				errorMessage = this.translate.instant('error_walletNotFound');
				break;
			case ManagedErrorCode.wordpressUserNotFound:
				// gestione speciale: mostro dialog con link alla registrazione CRM
				this.dialogUtils.openAdviseDialogWithLink(
					this.translate.instant('errore'),
					this.translate.instant('error_utenteNonTrovatoWordpress'),
					environment.crmUrl,
					this.translate.instant('registratiAlCRM'),
					'error'
				);
				return; // esco senza mostrare il dialog standard
			case ManagedErrorCode.wordpressClientError:
				errorMessage = this.translate.instant('error_wordpressClientError');
				break;
			case ManagedErrorCode.wordpressConnectionError:
				errorMessage = this.translate.instant('error_wordpressConnectionError');
				break;
			case ManagedErrorCode.noAuthCondivisione:
				errorMessage = this.translate.instant('error_noAuthCondivisione');
				break;
			case ManagedErrorCode.visuraNotFound:
				errorMessage = this.translate.instant('error_visuraNotFound');
				break;
			case ManagedErrorCode.visuraSoggettoNonCorrispondente:
				errorMessage = this.translate.instant('error_visuraSoggettoNonCorrispondente');
				break;
			case ManagedErrorCode.ricercaProvinceNonRiuscita: {
				// il backend manda la causa vera (stato dell'ultima lettura, timeout, errore della banca dati):
				// va mostrata, altrimenti resta solo un "non riuscita" su cui non si puo' fare niente
				const dettaglio: string = errorResponse.error?.title ?? errorResponse.error?.detail;
				errorMessage = this.translate.instant('error_ricercaProvinceNonRiuscita');
				if (dettaglio) {
					errorMessage += ' ' + dettaglio;
				}
				break;
			}
			case ManagedErrorCode.templateNonTrovato:
				errorMessage = this.translate.instant('error_templateNonTrovato');
				break;
		}

		// errori del giornale rettifiche: chiave tradotta in rettifiche.errori
		if (!errorMessage && errorKey) {
			const chiaveRettifiche = 'rettifiche.errori.' + errorKey;
			const tradotto: string = this.translate.instant(chiaveRettifiche);
			if (tradotto !== chiaveRettifiche) {
				// su residuo e quadratura il BE indica beni, importi e gruppi (in defaultMessage: il title diventa "Bad Request")
				const dettaglio: string = errorResponse.error?.defaultMessage;
				const conDettaglio = ['residuoInsufficiente', 'quadraturaNonRispettata'].includes(errorKey);
				errorMessage = conDettaglio && dettaglio ? `${tradotto} ${dettaglio}` : tradotto;
			}
		}

		if (!errorMessage) {
			// errore NON gestito --> mostro messaggio di errore generico (in formato HTML)
			this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), this.translate.instant('fatal_error'), 'error', true);
		} else {
			this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), errorMessage);
		}
	}

	private logoutAndGoToLogin(isSessionExpired = false) {
		this.closeAllDialogs();
		if (isSessionExpired) {
			this.dialogUtils.openAdviseDialog(this.translate.instant('errore'), this.translate.instant('error_401'));
		}
		this.accountService.logout();
	}

	private closeAllDialogs() {
		this.dialogService.dialogComponentRefMap.forEach(dialog => dialog.destroy()); // x chiusura di tutti i p-dialog eventualmente aperti
	}

}

